import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeUp } from '@/lib/motion';
import { Calculator, ArrowRight, TrendingDown, Loader2, Check, X } from 'lucide-react';
import { submitLead } from '@/lib/api';
import { toast } from 'sonner';

const TCOCalculator = ({ sourcePage = '/servicios/arriendo-leasing' }) => {
  const [inputs, setInputs] = useState({
    equipos: 20,
    costoEquipo: 2500000,
    plazo: 36,
  });
  const [result, setResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);

  const calculate = () => {
    const { equipos, costoEquipo, plazo } = inputs;
    const totalCapex = equipos * costoEquipo;
    const soporteAnual = totalCapex * 0.12; // 12% annual support
    const depreciacion = totalCapex / 5; // 5 years
    const tcoCapex3a = totalCapex + (soporteAnual * (plazo / 12));

    const cuotaMensual = costoEquipo * 0.045; // ~4.5% monthly of equipment value
    const totalOpex = equipos * cuotaMensual * plazo;
    const ahorroVsCapex = tcoCapex3a - totalOpex;

    setResult({
      totalCapex: Math.round(totalCapex),
      soporteAnual: Math.round(soporteAnual),
      tcoCapex: Math.round(tcoCapex3a),
      cuotaMensual: Math.round(cuotaMensual),
      totalOpex: Math.round(totalOpex),
      cuotaTotalMensual: Math.round(equipos * cuotaMensual),
      ahorro: Math.round(ahorroVsCapex),
      ahorroPercent: Math.round((ahorroVsCapex / tcoCapex3a) * 100),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const fd = new FormData(e.target);
    try {
      await submitLead({
        name: fd.get('name'),
        email: fd.get('email'),
        company: fd.get('company'),
        interest: 'arriendo-leasing',
        message: `TCO Calculator: ${JSON.stringify(inputs)} -> Ahorro OPEX: $${result?.ahorro?.toLocaleString('es-CL')}`,
        source_page: sourcePage,
        form_type: 'tco_calculator',
        honeypot: '',
      });
      toast.success('Solicitud enviada. Te contactaremos con una propuesta personalizada.');
      setShowForm(false);
    } catch {
      toast.error('Error al enviar.');
    } finally {
      setSending(false);
    }
  };

  const formatCLP = (n) => `$${Math.abs(n).toLocaleString('es-CL')}`;
  const inputClass = 'w-full px-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 focus:border-[#0EA5E9] transition-colors text-slate-900';

  return (
    <motion.div variants={fadeUp} className="rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] overflow-hidden" data-testid="tco-calculator">
      <div className="bg-[#0A2540] p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0EA5E9]/20 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-[#0EA5E9]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white" style={{fontFamily:'Syne,sans-serif'}}>Calculadora TCO: CAPEX vs OPEX</h3>
            <p className="text-xs text-slate-400">Compara el costo total de propiedad vs arriendo</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Cantidad de equipos</label>
            <input type="number" value={inputs.equipos} onChange={e => setInputs({...inputs, equipos: +e.target.value})} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Valor unitario (CLP)</label>
            <input type="number" value={inputs.costoEquipo} onChange={e => setInputs({...inputs, costoEquipo: +e.target.value})} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Plazo (meses)</label>
            <select value={inputs.plazo} onChange={e => setInputs({...inputs, plazo: +e.target.value})} className={inputClass}>
              <option value={12}>12 meses</option>
              <option value={24}>24 meses</option>
              <option value={36}>36 meses</option>
            </select>
          </div>
        </div>

        <button onClick={calculate} className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg transition-all" data-testid="tco-calculator-submit">
          <Calculator className="w-4 h-4" /> Comparar CAPEX vs OPEX
        </button>

        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
            <div className="accent-line w-16 mx-auto mb-6" />
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {/* CAPEX Column */}
              <div className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <X className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-semibold text-slate-700">CAPEX (Compra)</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Inversión inicial</span><span className="font-medium">{formatCLP(result.totalCapex)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Soporte ({inputs.plazo / 12}a)</span><span className="font-medium">{formatCLP(result.soporteAnual * (inputs.plazo / 12))}</span></div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between"><span className="font-semibold">TCO Total</span><span className="font-bold text-slate-900">{formatCLP(result.tcoCapex)}</span></div>
                </div>
              </div>
              {/* OPEX Column */}
              <div className="p-4 rounded-xl border-2 border-[#0EA5E9] bg-sky-50">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-4 h-4 text-[#0EA5E9]" />
                  <span className="text-sm font-semibold text-[#0A2540]">OPEX (Arriendo)</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Inversión inicial</span><span className="font-medium text-emerald-600">$0</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Cuota mensual total</span><span className="font-medium">{formatCLP(result.cuotaTotalMensual)}</span></div>
                  <div className="border-t border-sky-200 pt-2 flex justify-between"><span className="font-semibold">Total {inputs.plazo}m</span><span className="font-bold text-[#0A2540]">{formatCLP(result.totalOpex)}</span></div>
                </div>
              </div>
            </div>

            {result.ahorro > 0 && (
              <div className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <TrendingDown className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                <div className="text-xl font-bold text-emerald-700" style={{fontFamily:'Syne,sans-serif'}}>{formatCLP(result.ahorro)} menos</div>
                <div className="text-xs text-emerald-600">Ahorro con OPEX vs CAPEX ({result.ahorroPercent}% menos)</div>
              </div>
            )}

            <p className="text-[10px] text-slate-400 text-center mt-3">*Estimación referencial. Las cuotas reales pueden variar según configuración y servicios incluidos.</p>

            {showForm && (
              <form onSubmit={handleSubmit} className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
                <p className="text-xs font-medium text-slate-700">Solicita tu cotización personalizada de arriendo:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input name="name" placeholder="Nombre" required className={inputClass} />
                  <input name="email" type="email" placeholder="Email" required className={inputClass} />
                  <input name="company" placeholder="Empresa" className={inputClass} />
                </div>
                <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#0A2540] rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Solicitar cotización <ArrowRight className="w-3.5 h-3.5" /></>}
                </button>
              </form>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default TCOCalculator;
